import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AppShell } from '@/shared/ui/app-shell'
import { PageHeader, PageHeaderSpacer } from '@/shared/ui/page-header'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { requireAuth } from '@/shared/lib/router'
import { useOrderById, usePublicOrder, useClaimOrder, useCancelOrder, type OrderStatus } from '@/features/orders/api/use-orders'
import { Skeleton, Button, OptimizedImage } from '@/shared/ui'
import {
  Package, Clock, CheckCircle, XCircle, Truck, Store,
  MessageSquare, AlertTriangle, MapPin, Building2, Calendar,
  CreditCard, ArrowRight, ExternalLink, User, Phone
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { formatPrice, formatDate } from '@/shared/lib/format'
import { useState } from 'react'

export const Route = createFileRoute('/order/$orderId')({
  component: OrderDetailPage,
})

// ─── Статусы ────────────────────────────────────────────
const STATUS_CONFIG: Record<OrderStatus, {
  label: string
  icon: typeof Clock
  color: string
  bgColor: string
  step: number
}> = {
  pending_payment: { label: 'Ожидает оплаты', icon: CreditCard,  color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-900/30', step: 0 },
  new:             { label: 'Принят',          icon: Clock,       color: 'text-amber-600 dark:text-amber-400',  bgColor: 'bg-amber-100 dark:bg-amber-900/30',  step: 1 },
  confirmed:       { label: 'Подтверждён',     icon: CheckCircle, color: 'text-blue-600 dark:text-blue-400',    bgColor: 'bg-blue-100 dark:bg-blue-900/30',    step: 2 },
  in_progress:     { label: 'Собирается',      icon: Package,     color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900/30', step: 3 },
  ready:           { label: 'Готов к выдаче',   icon: Package,     color: 'text-green-600 dark:text-green-400',  bgColor: 'bg-green-100 dark:bg-green-900/30',  step: 4 },
  completed:       { label: 'Завершён',         icon: CheckCircle, color: 'text-gray-500',                      bgColor: 'bg-gray-100 dark:bg-gray-800',       step: 5 },
  cancelled:       { label: 'Отменён',          icon: XCircle,     color: 'text-red-500',                       bgColor: 'bg-red-100 dark:bg-red-900/30',      step: -1 },
}

const ORDER_STEPS = [
  { label: 'Принят', shortLabel: 'Принят' },
  { label: 'Подтверждён', shortLabel: 'Подтв.' },
  { label: 'Собирается', shortLabel: 'Сборка' },
  { label: 'Готов', shortLabel: 'Готов' },
  { label: 'Завершён', shortLabel: 'Готово' },
]

// ─── Степпер ────────────────────────────────────────────
function OrderStepper({ currentStep }: { currentStep: number }) {
  if (currentStep < 1) return null // Не показываем для pending_payment и cancelled

  // Маппинг: step 1=Принят → index 0, step 5=Завершён → index 4
  const activeIndex = currentStep - 1

  return (
    <div className="flex items-center gap-1 px-1">
      {ORDER_STEPS.map((step, i) => {
        const isDone = i < activeIndex
        const isActive = i === activeIndex

        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
            <div className={cn(
              "w-full h-1.5 rounded-full transition-colors",
              isDone ? 'bg-green-500' : isActive ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'
            )} />
            <span className={cn(
              "text-[9px] font-medium leading-none",
              isDone ? 'text-green-600 dark:text-green-400' :
              isActive ? 'text-blue-600 dark:text-blue-400' :
              'text-gray-400 dark:text-gray-500'
            )}>
              {step.shortLabel}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Способ оплаты ──────────────────────────────────────
function getPaymentMethodLabel(method: string): string {
  switch (method) {
    case 'bank_card':  return 'Банковская карта'
    case 'sbp':        return 'СБП'
    case 'yoo_money':  return 'ЮMoney'
    case 'cash':       return 'При получении'
    case 'gift_card':  return 'Подарочная карта'
    default:           return method
  }
}

// ─── Pluralize days ─────────────────────────────────────
function pluralizeDays(n: number): string {
  const abs = Math.abs(n) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return 'дней'
  if (last === 1) return 'день'
  if (last >= 2 && last <= 4) return 'дня'
  return 'дней'
}

// ─── Страница ───────────────────────────────────────────
function OrderDetailPage() {
  const { orderId } = Route.useParams()
  const { initData, hapticFeedback } = useTelegram()
  const navigate = useNavigate()
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  useBackButton(true, () => navigate({ to: '/orders' }))
  requireAuth(initData)

  const { data: ownOrder, isLoading, error } = useOrderById(orderId)
  const isNotOwned = !!error && !isLoading
  const { data: publicOrder, isLoading: publicLoading } = usePublicOrder(orderId, isNotOwned)
  const claimOrder = useClaimOrder()
  const cancelOrder = useCancelOrder()

  const order = ownOrder || publicOrder
  const isUnclaimed = !ownOrder && !!publicOrder

  const handleClaim = async () => {
    if (!orderId) return
    hapticFeedback?.impactOccurred('medium')
    try {
      await claimOrder.mutateAsync(orderId)
      hapticFeedback?.notificationOccurred('success')
    } catch {
      hapticFeedback?.notificationOccurred('error')
    }
  }

  const handleCancel = async () => {
    if (!order) return
    hapticFeedback?.impactOccurred('medium')
    try {
      await cancelOrder.mutateAsync({ orderId: order.id })
      hapticFeedback?.notificationOccurred('success')
      setShowCancelConfirm(false)
    } catch {
      hapticFeedback?.notificationOccurred('error')
    }
  }

  // Загрузка
  if (isLoading || (isNotOwned && publicLoading)) {
    return (
      <AppShell>
        <PageHeader title="Загрузка..." hideBackButton />
        <PageHeaderSpacer />
        <div className="content-container space-y-4 pt-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      </AppShell>
    )
  }

  if (!order) {
    return (
      <AppShell>
        <PageHeader title="Заказ" hideBackButton />
        <PageHeaderSpacer />
        <div className="content-container text-center py-16">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Заказ не найден</p>
          <Button variant="secondary" className="mt-4" onClick={() => navigate({ to: '/orders' })}>
            К списку заказов
          </Button>
        </div>
      </AppShell>
    )
  }

  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.new
  const StatusIcon = status.icon
  const isPickup = order.deliveryType === 'pickup'
  const isCdekPvz = order.deliveryType === 'cdek_pvz'
  const isCdekDoor = order.deliveryType === 'cdek_door'
  const isCdek = isCdekPvz || isCdekDoor
  const canCancel = order.status === 'pending_payment'

  return (
    <AppShell>
      <PageHeader title={`Заказ #${order.id.slice(0, 8)}`} hideBackButton />
      <PageHeaderSpacer />

      <main className="content-container pb-6 space-y-3 pt-4">

        {/* ─── Статус + степпер ─── */}
        <div className="bg-white dark:bg-dark-card rounded-xl overflow-hidden">
          <div className={cn("px-4 py-3 flex items-center gap-3", status.bgColor)}>
            <StatusIcon className={cn("w-6 h-6 shrink-0", status.color)} />
            <div className="min-w-0">
              <p className={cn("font-bold text-[15px]", status.color)}>{status.label}</p>
              <p className="text-[11px] text-muted-foreground">{formatDate(order.createdAt)}</p>
            </div>
          </div>
          {order.status !== 'cancelled' && order.status !== 'pending_payment' && (
            <div className="px-4 py-3">
              <OrderStepper currentStep={status.step} />
            </div>
          )}
        </div>

        {/* ─── Привязка гостевого заказа ─── */}
        {isUnclaimed && (
          <div className="rounded-xl p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
              Это заказ с сайта
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
              Привяжите к аккаунту, чтобы получать уведомления о статусе
            </p>
            <Button className="w-full" onClick={handleClaim} disabled={claimOrder.isPending}>
              {claimOrder.isPending ? 'Привязываем...' : 'Привязать к моему аккаунту'}
            </Button>
          </div>
        )}

        {/* ─── Кнопка оплаты ─── */}
        {order.status === 'pending_payment' && order.paymentUrl && (
          <Button
            className="w-full"
            onClick={() => {
              hapticFeedback?.impactOccurred('medium')
              if (window.Telegram?.WebApp?.openLink) {
                window.Telegram.WebApp.openLink(order.paymentUrl!)
              } else {
                window.open(order.paymentUrl!, '_blank')
              }
            }}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Перейти к оплате
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}

        {/* ─── Причина отмены ─── */}
        {order.status === 'cancelled' && order.cancelReason && (
          <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
            <p className="text-sm text-red-600 dark:text-red-400">
              <span className="font-medium">Причина:</span> {order.cancelReason}
            </p>
          </div>
        )}

        {/* ─── Доставка ─── */}
        <div className="bg-white dark:bg-dark-card rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            {isPickup ? (
              <Store className="w-5 h-5 text-amber-500" />
            ) : isCdekDoor ? (
              <Truck className="w-5 h-5 text-blue-500" />
            ) : (
              <MapPin className="w-5 h-5 text-emerald-500" />
            )}
            <h3 className="font-bold text-[14px]">
              {isPickup ? 'Самовывоз' : isCdekDoor ? 'Курьер до двери' : 'Пункт выдачи'}
            </h3>
            {isCdek && (
              <span className="text-[9px] font-bold text-[#00B33C] bg-[#00B33C]/10 px-1.5 py-0.5 rounded">СДЭК</span>
            )}
          </div>

          {/* Получатель */}
          {order.recipientName && (
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm text-ink dark:text-white font-medium">{order.recipientName}</span>
            </div>
          )}

          {/* СДЭК ПВЗ */}
          {isCdekPvz && (
            <div className="space-y-2">
              {order.cdekPointName && (
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-ink dark:text-white">{order.cdekPointName}</p>
                    {order.cdekPointAddress && (
                      <p className="text-xs text-muted-foreground">{order.cdekPointAddress}</p>
                    )}
                  </div>
                </div>
              )}
              {order.cdekPointWorkTime && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  <span>{order.cdekPointWorkTime}</span>
                </div>
              )}
              {order.cdekDeliveryMinDays != null && order.cdekDeliveryMaxDays != null && (
                <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                  <Calendar className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    {order.cdekDeliveryMinDays === order.cdekDeliveryMaxDays
                      ? `${order.cdekDeliveryMinDays} ${pluralizeDays(order.cdekDeliveryMinDays)}`
                      : `${order.cdekDeliveryMinDays}–${order.cdekDeliveryMaxDays} ${pluralizeDays(order.cdekDeliveryMaxDays)}`
                    }
                  </span>
                </div>
              )}
            </div>
          )}

          {/* СДЭК Курьер */}
          {isCdekDoor && (
            <div className="space-y-2">
              {order.cdekDoorStreet && (
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <p className="text-ink dark:text-white">
                      {order.cdekDoorStreet}{order.cdekDoorHouse ? `, д. ${order.cdekDoorHouse}` : ''}
                      {order.cdekDoorBuilding ? `, корп. ${order.cdekDoorBuilding}` : ''}
                      {order.cdekDoorApartment ? `, кв. ${order.cdekDoorApartment}` : ''}
                    </p>
                    {(order.cdekDoorEntrance || order.cdekDoorFloor || order.cdekDoorIntercom) && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {[
                          order.cdekDoorEntrance && `подъезд ${order.cdekDoorEntrance}`,
                          order.cdekDoorFloor && `этаж ${order.cdekDoorFloor}`,
                          order.cdekDoorIntercom && `домофон ${order.cdekDoorIntercom}`,
                        ].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {order.cdekPointCity && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  <span>{order.cdekPointCity}</span>
                </div>
              )}
              {order.cdekDeliveryMinDays != null && order.cdekDeliveryMaxDays != null && (
                <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                  <Calendar className="w-3.5 h-3.5 shrink-0" />
                  <span>
                    {order.cdekDeliveryMinDays === order.cdekDeliveryMaxDays
                      ? `${order.cdekDeliveryMinDays} ${pluralizeDays(order.cdekDeliveryMinDays)}`
                      : `${order.cdekDeliveryMinDays}–${order.cdekDeliveryMaxDays} ${pluralizeDays(order.cdekDeliveryMaxDays)}`
                    }
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Phone className="w-3.5 h-3.5 shrink-0" />
                <span>Курьер свяжется по телефону в день доставки</span>
              </div>
            </div>
          )}

          {/* Самовывоз */}
          {isPickup && order.shippingAddress && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">{order.shippingAddress}</p>
            </div>
          )}

          {/* Стоимость доставки */}
          {isCdek && order.cdekDeliveryCost != null && order.cdekDeliveryCost > 0 && (
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-silk/50 dark:border-dark-border">
              <span className="text-xs text-muted-foreground">Стоимость доставки</span>
              <span className="text-sm font-semibold">{formatPrice(order.cdekDeliveryCost)}</span>
            </div>
          )}

          {/* СДЭК трекинг */}
          {isCdek && (
            <div className="pt-2 mt-2 border-t border-silk/50 dark:border-dark-border">
              {order.cdekOrderNumber ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Трек-номер</span>
                    <span className="text-sm font-bold font-mono">{order.cdekOrderNumber}</span>
                  </div>
                  <a
                    href={`https://www.cdek.ru/ru/tracking/?order_id=${order.cdekOrderNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-[#00B33C]/10 text-[#00B33C] text-sm font-semibold hover:bg-[#00B33C]/20 transition-colors"
                  >
                    <Truck className="w-4 h-4" />
                    Отследить на сайте СДЭК
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              ) : order.status !== 'cancelled' && (
                <p className="text-xs text-muted-foreground">
                  Трек-номер появится после отправки заказа
                </p>
              )}
            </div>
          )}

          {/* Комментарий */}
          {order.shippingComment && (
            <div className="flex items-start gap-2 text-sm pt-2 border-t border-silk/50 dark:border-dark-border mt-2">
              <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-muted-foreground">{order.shippingComment}</p>
            </div>
          )}
        </div>

        {/* ─── Товары ─── */}
        <div className="bg-white dark:bg-dark-card rounded-xl p-4">
          <h3 className="font-bold text-[14px] mb-3">Товары</h3>
          <div className="space-y-3">
            {order.items.map((item) => (
              <div key={item.id} className="flex gap-3">
                {item.productImage ? (
                  <OptimizedImage
                    src={item.productImage}
                    alt={item.productName}
                    preset="thumb"
                    wrapperClassName="w-14 h-14 rounded-lg bg-gray-100"
                    className="object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gray-100 dark:bg-dark-elevated flex items-center justify-center shrink-0">
                    <Package className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm line-clamp-2">{item.productName}</p>
                  {item.productSku && (
                    <p className="text-[10px] font-mono text-gray-400 dark:text-gray-500">
                      Арт. {item.productSku}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {item.quantity} шт.{item.weight ? ` × ${item.weight}г` : ''} · {formatPrice(item.unitPrice)}
                  </p>
                </div>
                <p className="font-semibold text-sm shrink-0">{formatPrice(item.subtotal)}</p>
              </div>
            ))}
          </div>

          {/* Итого */}
          <div className="border-t border-border mt-4 pt-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Товары</span>
              <span>{formatPrice(order.itemsAmount)}</span>
            </div>
            {order.shippingCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Доставка</span>
                <span>{formatPrice(order.shippingCost)}</span>
              </div>
            )}
            {isCdek && order.shippingCost === 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Доставка</span>
                <span className="text-green-600 dark:text-green-400">Бесплатно</span>
              </div>
            )}
            {order.giftCardAmount > 0 && (
              <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                <span>Подарочная карта</span>
                <span>−{formatPrice(order.giftCardAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base pt-2 border-t border-border">
              <span>Итого</span>
              <span>{formatPrice(order.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* ─── Оплата ─── */}
        {order.paymentStatus && order.paymentStatus !== 'pending' && (
          <div className="bg-white dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-primary" />
              <h3 className="font-bold text-[14px]">Оплата</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Статус</span>
                <span className={cn(
                  'font-medium',
                  order.paymentStatus === 'succeeded' && 'text-green-600',
                  order.paymentStatus === 'canceled' && 'text-red-500',
                  order.paymentStatus === 'refunded' && 'text-amber-600',
                )}>
                  {order.paymentStatus === 'succeeded' && 'Оплачен'}
                  {order.paymentStatus === 'canceled' && 'Отменён'}
                  {order.paymentStatus === 'refunded' && 'Возвращён'}
                </span>
              </div>
              {order.paymentMethod && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Способ</span>
                  <span>{getPaymentMethodLabel(order.paymentMethod)}</span>
                </div>
              )}
              {order.paidAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Дата</span>
                  <span>{formatDate(order.paidAt)}</span>
                </div>
              )}
              {order.refundedAmount && Number(order.refundedAmount) > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Возврат</span>
                  <span className="text-amber-600 font-medium">{formatPrice(Number(order.refundedAmount))}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Отмена ─── */}
        {canCancel && (
          <div>
            {!showCancelConfirm ? (
              <Button
                variant="secondary"
                className="w-full text-red-500 border-red-200 dark:border-red-800"
                onClick={() => setShowCancelConfirm(true)}
              >
                Отменить заказ
              </Button>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Вы уверены, что хотите отменить заказ?
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" className="flex-1" onClick={() => setShowCancelConfirm(false)}>
                    Нет
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-1 bg-red-500 hover:bg-red-600"
                    onClick={handleCancel}
                    disabled={cancelOrder.isPending}
                  >
                    {cancelOrder.isPending ? 'Отмена...' : 'Да, отменить'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── Помощь ─── */}
        <div className="bg-white dark:bg-dark-card rounded-xl p-4">
          <h3 className="font-bold text-[14px] mb-2">Нужна помощь?</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Свяжитесь с нами для уточнения деталей заказа
          </p>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              hapticFeedback?.impactOccurred('light')
              window.open('https://t.me/drakonisoroka', '_blank')
            }}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Написать в Telegram
          </Button>
        </div>
      </main>
    </AppShell>
  )
}
